import { useState, useMemo, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { InsightsChatPanel } from "@/components/dashboard/InsightsChatPanel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DollarSign,
  TrendingUp,
  AlertCircle,
  FileText,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Zap,
} from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { parseISO, differenceInDays, subMonths, subYears } from "date-fns";
import { InvoiceLineChart } from "@/components/charts/InvoiceLineChart";
import type { Invoice } from "@/types";

// ── Constants ─────────────────────────────────────────────────────────────────
const DEMO_HURDLE_RATE = 8; // % — typical corporate cash hurdle rate

type TimePeriod = "1M" | "3M" | "6M" | "1Y" | "All";

const TIME_PERIODS: { label: string; value: TimePeriod }[] = [
  { label: "1M", value: "1M" },
  { label: "3M", value: "3M" },
  { label: "6M", value: "6M" },
  { label: "1Y", value: "1Y" },
  { label: "All", value: "All" },
];

function getPeriodStart(period: TimePeriod): Date | null {
  const now = new Date();
  if (period === "1M") return subMonths(now, 1);
  if (period === "3M") return subMonths(now, 3);
  if (period === "6M") return subMonths(now, 6);
  if (period === "1Y") return subYears(now, 1);
  return null;
}

type ChartInvoice = Pick<
  Invoice,
  | "id"
  | "payment_amount"
  | "discounted_payment_amount"
  | "discount_amount"
  | "status"
  | "created_at"
  | "payment_due_date"
> & { invoice_file_url?: string | null; invoice_file_name?: string | null };

export interface InsightsTabProps {
  totalSaved: number;
  dollarsAccelerated: number;
  earlyPaymentBudget: number;
  usedBudget: number;
  availableBudget: number;
  /** % of offered invoices accepted (0–100) */
  captureRate: number;
  /** $ left uncaptured: declined invoices + budget-blocked pending */
  missedSavings: number;
  paymentWindowDays: number;
  currentApr: number;
  budgetPeriodStart?: string | null;
  budgetPeriodEnd?: string | null;
  invoicesForChart: ChartInvoice[];
  historicalInvoicesForChart?: ChartInvoice[];
  /** Full invoice list for drill-down modals */
  allInvoices?: Invoice[];
  pendingInvoices?: Invoice[];
}

function fmt(amount: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

// ─── Missed Savings Modal ────────────────────────────────────────────────────

interface MissedCategory {
  id: string;
  label: string;
  dotColor: string;
  count: number;
  total: number;
  invoices: Invoice[];
  demoMode?: boolean;
}

interface CategoryRowProps {
  category: MissedCategory;
  expanded: boolean;
  onToggle: () => void;
}

function CategoryRow({ category, expanded, onToggle }: CategoryRowProps) {
  const hasItems = category.invoices.length > 0 || category.demoMode;
  return (
    <div className="border border-border/50 rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/40 transition-colors"
        onClick={hasItems ? onToggle : undefined}
        disabled={!hasItems}
      >
        <span
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: category.dotColor }}
        />
        <span className="flex-1 text-xs font-medium text-foreground">{category.label}</span>
        <span className="text-xs text-muted-foreground">
          {category.count} inv · {fmt(category.total)}
        </span>
        {hasItems ? (
          expanded ? (
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          )
        ) : (
          <span className="w-3.5 h-3.5 flex-shrink-0" />
        )}
      </button>
      {expanded && hasItems && (
        <div className="border-t border-border/50 bg-muted/20">
          {category.demoMode && category.invoices.length === 0 ? (
            <p className="px-4 py-3 text-xs text-muted-foreground italic">
              Demo data — {category.count} invoices totaling {fmt(category.total)}
            </p>
          ) : (
            <ul className="divide-y divide-border/30">
              {category.invoices.map((inv) => (
                <li key={inv.id} className="flex items-center justify-between px-4 py-2 gap-2">
                  <span className="text-xs text-foreground truncate max-w-[55%]">
                    {inv.supplier_name}
                  </span>
                  <span className="text-xs font-semibold text-foreground">
                    {fmt(inv.discount_amount ?? inv.payment_amount * 0.015)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export function InsightsTab({
  totalSaved,
  dollarsAccelerated,
  earlyPaymentBudget,
  usedBudget,
  availableBudget,
  captureRate,
  missedSavings: _missedSavings,
  paymentWindowDays,
  currentApr,
  budgetPeriodStart: _budgetPeriodStart,
  budgetPeriodEnd: _budgetPeriodEnd,
  invoicesForChart,
  historicalInvoicesForChart = [],
  allInvoices = [],
  pendingInvoices = [],
}: InsightsTabProps) {
  const [missedModalOpen, setMissedModalOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    declined: true,
    budget: false,
    noTerms: false,
    pending: false,
  });
  const [chartPeriod, setChartPeriod] = useState<TimePeriod>("All");

  const toggleCategory = useCallback((id: string) => {
    setExpandedCategories((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const totalOpportunity = totalSaved;
  const inconsistentSupplierCount = 3;

  // ── Category 1: Supplier declined ──
  const declinedInvoices = useMemo(
    () => allInvoices.filter((inv) => inv.status === "standard_payment"),
    [allInvoices]
  );
  const declinedTotal = useMemo(
    () =>
      declinedInvoices.reduce(
        (sum, inv) => sum + (inv.discount_amount ?? inv.payment_amount * 0.015),
        0
      ),
    [declinedInvoices]
  );

  // ── Category 2: Budget blocked ── (demo data)
  const BUDGET_BLOCKED_COUNT = 8;
  const BUDGET_BLOCKED_TOTAL = 34_215;

  // ── Category 3: No terms on file ── (demo data)
  const NO_TERMS_COUNT = 14;
  const NO_TERMS_TOTAL = 27_480;

  // ── Category 4: Offer not yet responded (stale pending ≥ 5 days) ──
  const stalePendingInvoices = useMemo(() => {
    const now = new Date();
    return allInvoices.filter(
      (inv) =>
        inv.status === "pending" &&
        inv.email_sent_at !== null &&
        differenceInDays(now, parseISO(inv.email_sent_at)) >= 5
    );
  }, [allInvoices]);
  const stalePendingTotal = useMemo(
    () =>
      stalePendingInvoices.reduce(
        (sum, inv) => sum + (inv.discount_amount ?? inv.payment_amount * 0.015),
        0
      ),
    [stalePendingInvoices]
  );

  // Hero and modal title always reflect unused budget (canonical source of truth)
  const missedSavings = availableBudget;

  // ── Payment Flow Pie Chart data ──────────────────────────────────────────
  // Fixed $115M annual AP spend breakdown — shows the full opportunity context
  const TOTAL_ANNUAL_SPEND = 115_000_000;
  const pieData = useMemo(() => {
    // Scale the $115M by time period (approximation)
    const cutoff = getPeriodStart(chartPeriod);
    const periodFraction = cutoff
      ? Math.min(1, (new Date().getTime() - cutoff.getTime()) / (365 * 24 * 60 * 60 * 1000))
      : 1;
    const periodSpend = Math.round(TOTAL_ANNUAL_SPEND * periodFraction);

    // $1M dynamic discounting (what's been accelerated through platform)
    const dynamicAmount = Math.min(dollarsAccelerated, periodSpend);
    // ~16% pre-negotiated fixed terms
    const prenegotiatedAmount = Math.round(periodSpend * 0.161);
    const trueStandard = periodSpend - dynamicAmount - prenegotiatedAmount;

    return [
      { name: "Dynamic discounting", value: dynamicAmount, color: "hsl(var(--success))" },
      { name: "Pre-negotiated terms", value: prenegotiatedAmount, color: "hsl(var(--primary))" },
      {
        name: "Standard payment",
        value: Math.max(0, trueStandard),
        color: "hsl(var(--muted-foreground))",
      },
    ].filter((d) => d.value > 0);
  }, [chartPeriod, dollarsAccelerated]);

  // ── Rate / return metrics ────────────────────────────────────────────────
  // Effective APR = configured rate on platform (what suppliers receive)
  const effectiveApr = currentApr;
  // Capital efficiency: annualised savings yield on deployed budget
  const capitalEfficiency = usedBudget > 0 ? (totalSaved / usedBudget) * 100 : 0;
  const vsHurdle = effectiveApr - DEMO_HURDLE_RATE;

  const categories: MissedCategory[] = [
    {
      id: "declined",
      label: "Supplier declined",
      dotColor: "hsl(var(--destructive))",
      count: declinedInvoices.length,
      total: declinedTotal,
      invoices: declinedInvoices,
    },
    {
      id: "budget",
      label: "Budget blocked",
      dotColor: "hsl(var(--warning))",
      count: BUDGET_BLOCKED_COUNT,
      total: BUDGET_BLOCKED_TOTAL,
      invoices: pendingInvoices,
      demoMode: pendingInvoices.length === 0,
    },
    {
      id: "noTerms",
      label: "No terms on file",
      dotColor: "hsl(var(--muted-foreground))",
      count: NO_TERMS_COUNT,
      total: NO_TERMS_TOTAL,
      invoices: [],
      demoMode: true,
    },
    {
      id: "pending",
      label: "Offer not yet responded",
      dotColor: "hsl(var(--primary))",
      count: stalePendingInvoices.length,
      total: stalePendingTotal,
      invoices: stalePendingInvoices,
    },
  ];

  // Average discount on spend
  const avgDiscountPct =
    dollarsAccelerated > 0 ? (totalSaved / (dollarsAccelerated + totalSaved)) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Hero: two-column opportunity vs. captured */}
      <div className="grid sm:grid-cols-2 gap-4">
        {/* Left: missed savings (opens breakdown modal) */}
        <Card
          className="glass-card p-6 border-destructive/20 bg-gradient-to-br from-card to-destructive/5 cursor-pointer hover:border-destructive/40 transition-colors"
          onClick={() => setMissedModalOpen(true)}
        >
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-destructive/10 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-destructive" />
            </div>
            <div>
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                Savings still on the table
              </p>
              <p className="text-3xl font-display font-bold text-foreground tracking-tight mt-0.5">
                {fmt(missedSavings)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Declined + budget-blocked + no terms on file
              </p>
              <p className="text-[10px] text-primary mt-1 font-medium">View breakdown →</p>
            </div>
          </div>
        </Card>

        {/* Right: captured so far + progress */}
        <Card className="glass-card p-6 border-success/20 bg-gradient-to-br from-card to-success/5">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-success/10 flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-5 h-5 text-success" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                Savings Captured So Far
              </p>
              <p className="text-3xl font-display font-bold text-foreground tracking-tight mt-0.5">
                {fmt(totalSaved)}
              </p>
              <div className="mt-2 space-y-1">
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span>{captureRate.toFixed(0)}% capture rate</span>
                  {totalOpportunity > 0 && <span>{fmt(totalOpportunity)} captured</span>}
                </div>
                <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-success rounded-full transition-all"
                    style={{ width: `${Math.min(captureRate, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Insight KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {/* Invoice value accelerated */}
        <Card className="glass-card p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Invoice value accelerated
            </p>
            <Zap className="w-4 h-4 text-primary/70" />
          </div>
          <p className="text-xl font-display font-bold text-foreground">
            {fmt(dollarsAccelerated)}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {captureRate.toFixed(1)}% capture rate · YTD
          </p>
          <div className="mt-2 w-full h-1 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full"
              style={{ width: `${Math.min(captureRate, 100)}%` }}
            />
          </div>
        </Card>

        {/* Avg discount on spend */}
        <Card className="glass-card p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Avg discount on spend
            </p>
            <TrendingUp className="w-4 h-4 text-success/70" />
          </div>
          <p className="text-xl font-display font-bold text-foreground">
            {avgDiscountPct.toFixed(2)}%
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {fmt(totalSaved)} saved on {fmt(dollarsAccelerated + totalSaved)} accelerated
          </p>
        </Card>

        {/* Inconsistent terms */}
        <Card className="glass-card p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Inconsistent terms
            </p>
            <FileText className="w-4 h-4 text-warning/70" />
          </div>
          <p className="text-xl font-display font-bold text-foreground">
            {inconsistentSupplierCount}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Suppliers with conflicting term data
          </p>
        </Card>
      </div>

      {/* Payment flow pie chart + rate metrics */}
      <Card className="glass-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display font-semibold text-foreground text-sm tracking-tight">
              Payment Flow Breakdown
            </h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">Where your AP spend is going</p>
          </div>
          {/* Time period selector */}
          <div className="flex gap-0.5 bg-muted/60 rounded-lg p-0.5">
            {TIME_PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setChartPeriod(p.value)}
                className={`text-[11px] px-2.5 py-1 rounded-md font-medium transition-colors ${
                  chartPeriod === p.value
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 items-center">
          {/* Pie */}
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius="55%"
                  outerRadius="80%"
                  paddingAngle={2}
                  dataKey="value"
                >
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} stroke="transparent" />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) => [fmt(value), ""]}
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Legend + rate metrics */}
          <div className="space-y-4">
            {/* Segments */}
            <div className="space-y-2">
              {pieData.map((d) => {
                const total = pieData.reduce((s, x) => s + x.value, 0);
                const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : "0.0";
                return (
                  <div key={d.name} className="flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ background: d.color }}
                    />
                    <span className="text-xs text-muted-foreground flex-1">{d.name}</span>
                    <span className="text-xs font-semibold text-foreground tabular-nums">
                      {pct}%
                    </span>
                    <span className="text-xs text-muted-foreground tabular-nums w-20 text-right">
                      {fmt(d.value)}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Rate / return vs hurdle */}
            <div className="border-t border-border/50 pt-3 space-y-2">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Rate of return
              </p>
              {[
                {
                  label: "Effective APR",
                  value: `${effectiveApr}%`,
                  color: "text-foreground",
                },
                {
                  label: "Capital efficiency",
                  value: `${capitalEfficiency.toFixed(1)}%`,
                  color: "text-success",
                },
                {
                  label: "Hurdle rate",
                  value: `${DEMO_HURDLE_RATE}%`,
                  color: "text-muted-foreground",
                },
                {
                  label: "APR vs. hurdle",
                  value: `${vsHurdle >= 0 ? "+" : ""}${vsHurdle.toFixed(1)}%`,
                  color: vsHurdle >= 0 ? "text-success" : "text-destructive",
                },
              ].map((row) => (
                <div key={row.label} className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">{row.label}</span>
                  <span className={`text-xs font-semibold font-display tabular-nums ${row.color}`}>
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Top opportunities callout */}
      {missedSavings > 0 && (
        <Card className="glass-card p-5 border-primary/20 bg-gradient-to-r from-primary/5 to-transparent">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center">
                  <TrendingUp className="w-3 h-3 text-primary" />
                </span>
                Where to focus next
              </p>
              <ul className="space-y-1.5 text-xs text-muted-foreground">
                <li className="flex items-center gap-2">
                  <ArrowRight className="w-3 h-3 text-primary flex-shrink-0" />
                  Switch to the <span className="font-semibold text-foreground">Optimize</span> tab
                  to accelerate pending invoices
                </li>
                <li className="flex items-center gap-2">
                  <ArrowRight className="w-3 h-3 text-primary flex-shrink-0" />
                  Review {inconsistentSupplierCount} suppliers with inconsistent payment terms in
                  the Supplier Terms Explorer
                </li>
                <li className="flex items-center gap-2">
                  <ArrowRight className="w-3 h-3 text-primary flex-shrink-0" />
                  Increasing budget by {fmt(10000)} could unlock an additional{" "}
                  {fmt(Math.round(missedSavings * 0.08))} in savings
                </li>
              </ul>
            </div>
            <Badge
              variant="outline"
              className="text-[10px] text-primary border-primary/30 flex-shrink-0"
            >
              DiscoRun AI
            </Badge>
          </div>
        </Card>
      )}

      {/* Trend chart */}
      <InvoiceLineChart
        invoices={invoicesForChart}
        historicalInvoices={historicalInvoicesForChart}
      />

      {/* Program summary */}
      <Card className="glass-card p-5 space-y-4">
        <div>
          <h3 className="font-display font-semibold text-foreground text-sm tracking-tight">
            Payment term optimization summary
          </h3>
          <p className="text-[11px] text-muted-foreground mt-1">
            High-level performance indicators
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-0">
          {[
            { label: "Acceleration budget", value: fmt(earlyPaymentBudget) },
            { label: "Budget utilized", value: fmt(usedBudget) },
            { label: "Budget remaining", value: fmt(availableBudget) },
            { label: "Capture rate", value: `${captureRate.toFixed(1)}%` },
            { label: "Payment minimum", value: `${paymentWindowDays} days` },
            { label: "Current APR", value: `${currentApr}%` },
          ].map((item) => (
            <div
              key={item.label}
              className="flex items-center justify-between py-2 border-b border-border/50 last:border-0"
            >
              <span className="text-xs text-muted-foreground capitalize">{item.label}</span>
              <span className="text-sm font-semibold text-foreground font-display">
                {item.value}
              </span>
            </div>
          ))}
        </div>
      </Card>

      {/* Ask DiscoRun AI chat panel */}
      <InsightsChatPanel
        captureRate={captureRate}
        missedSavings={missedSavings}
        totalSaved={totalSaved}
        availableBudget={availableBudget}
        allInvoices={allInvoices}
      />

      {/* ── Categorized missed savings modal ── */}
      <Dialog open={missedModalOpen} onOpenChange={setMissedModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-base">
              Uncaptured savings — {fmt(missedSavings)}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Breakdown of why early payment discounts were not captured this period.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {categories.map((cat) => (
              <CategoryRow
                key={cat.id}
                category={cat}
                expanded={!!expandedCategories[cat.id]}
                onToggle={() => toggleCategory(cat.id)}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
